import angular from 'angular';
import { NsjRoutingType } from '../../../core/nsj-core';
import { LoadParams } from '../../../shared/models/loadparams.model';
import {
    IUploadDocumentoConstructor,
    IUploadDocumentoCreate,
    IUploadDocumentoGet,
    IUploadDocumentoHistorico,
    IUploadDocumentoItem,
    IUploadDocumentoListItem,
    IUploadDocumentoLog,
    IUploadDocumentoOffset,
    IUploadDocumentoPreview,
    IUploadDocumentoReversao,
} from './models/upload-documentos-em-lote.service.model';

export class UploadDocumentosEmLoteService {

    static $inject = ['$rootScope', '$http', '$q', 'nsjRouting'];

    public entities: IUploadDocumentoListItem[] = [];
    public loaded = false;
    public constructors: IUploadDocumentoConstructor = {};
    public after: IUploadDocumentoOffset = {};
    public loadParams = new LoadParams();
    public loading_deferred: angular.IDeferred<unknown>;
    public filter = '';

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $http: angular.IHttpService,
        public $q: angular.IQService,
        public nsjRouting: NsjRoutingType,
    ) { }

    reload() {
        if (this.loading_deferred) {
            this.loading_deferred.resolve();
        }

        this.after = {};
        this.loaded = false;
        this.entities = [];
        this.loadParams = new LoadParams();
        this.loadParams.to_load = 1;

        return this.load();
    }

    loadMore() {
        this.loadParams.to_load = 1;
        this.load();
    }

    _load(constructors: IUploadDocumentoConstructor, offset: IUploadDocumentoOffset, filter: string) {
        this.loading_deferred = this.$q.defer();

        return this.$q<IUploadDocumentoListItem[]>((resolve, reject) => {
            this.$http.get<IUploadDocumentoListItem[]>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_index',
                    angular.extend({}, constructors, {
                        'offset': offset,
                        'filter': filter
                    }),
                true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => {
                resolve(response.data);
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    load() {
        if (!this.loadParams.busy && !this.loadParams.finished && this.loadParams.to_load > 0) {
            this.loadParams.busy = true;

            this._load(this.constructors, this.after, this.filter)
                .then((data) => {
                    if (data.length > 0) {
                        for (let i = 0; i < data.length; i++) {
                            this.entities.push(data[i]);
                        }
                        this.after = {
                            uploaddocumento: this.entities[this.entities.length - 1]['uploaddocumento']
                        };
                    }

                    // menos de uma página cheia significa que não há mais o que buscar.
                    if (data.length < 20) {
                        this.loadParams.finished = true;
                    }

                    this.loaded = true;
                    this.loadParams.to_load--;
                    this.$rootScope.$broadcast('meurh_uploadsdocumentos_index_finished', this.entities);
                })
                .catch((error) => {
                    if (error.xhrStatus !== 'abort') {
                        this.loadParams.finished = true;
                    }
                })
                .finally(() => {
                    if (this.loaded || this.loadParams.finished) {
                        this.loadParams.busy = false;
                    }
                    this.load();
                });
        }

        return this.entities;
    }

    preview(entity: IUploadDocumentoCreate) {
        this.loading_deferred = this.$q.defer();
        return this.$q((resolve: angular.IQResolveReject<IUploadDocumentoPreview>, reject) => {
            this.$http.post<IUploadDocumentoPreview>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_preview', {}, true), entity, {
                timeout: this.loading_deferred.promise
            })
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_preview_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_preview_error', response);
                reject(response);
            });
        });
    }

    criar(entity: IUploadDocumentoCreate) {
        this.loading_deferred = this.$q.defer();
        return this.$q((resolve: angular.IQResolveReject<IUploadDocumentoGet>, reject) => {
            this.$http.post<IUploadDocumentoGet>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_insert', {}, true), entity, {
                timeout: this.loading_deferred.promise
            })
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_insert_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_insert_error', response);
                reject(response);
            });
        });
    }

    get(id: string) {
        this.loading_deferred = this.$q.defer();
        return this.$q<IUploadDocumentoGet>((resolve, reject) => {
            this.$http.get<IUploadDocumentoGet>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_get', { id }, true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_get_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_get_error', response);
                reject(response);
            });
        });
    }

    getLog(uploaddocumento: string) {
        this.loading_deferred = this.$q.defer();
        return this.$q<IUploadDocumentoLog>((resolve, reject) => {
            this.$http.get<IUploadDocumentoLog>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_log', { uploaddocumento }, true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_log_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_log_error', response);
                reject(response);
            });
        });
    }

    getHistorico(uploaddocumento: string) {
        this.loading_deferred = this.$q.defer();
        return this.$q<IUploadDocumentoHistorico[]>((resolve, reject) => {
            this.$http.get<IUploadDocumentoHistorico[]>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_historico', { uploaddocumento }, true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_historico_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_historico_error', response);
                reject(response);
            });
        });
    }

    /** Itens do lote: um por colaborador, com situação e motivo. */
    getItens(uploaddocumento: string) {
        this.loading_deferred = this.$q.defer();
        return this.$q<IUploadDocumentoItem[]>((resolve, reject) => {
            this.$http.get<IUploadDocumentoItem[]>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_trabalhadores_afetados', { uploaddocumento }, true),
                { timeout: this.loading_deferred.promise }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_trabalhadores_afetados_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_trabalhadores_afetados_error', response);
                reject(response);
            });
        });
    }

    reverter(uploaddocumento: string, entity: IUploadDocumentoReversao) {
        this.loading_deferred = this.$q.defer();
        return this.$q((resolve: angular.IQResolveReject<IUploadDocumentoGet>, reject) => {
            this.$http.post<IUploadDocumentoGet>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_reversao', { uploaddocumento }, true), entity, {
                timeout: this.loading_deferred.promise
            })
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_reversao_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_reversao_error', response);
                reject(response);
            });
        });
    }

    exportarXlsx(uploaddocumento: string) {
        this.loading_deferred = this.$q.defer();
        return this.$q<Blob>((resolve, reject) => {
            this.$http.get<Blob>(
                this.nsjRouting.generate('meurh_uploadsdocumentos_exportacao', { uploaddocumento }, true), {
                    timeout: this.loading_deferred.promise,
                    responseType: 'blob',
                }
            )
            .then((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_exportacao_loaded', response.data);
                resolve(response.data);
            })
            .catch((response) => {
                this.$rootScope.$broadcast('meurh_uploadsdocumentos_exportacao_error', response);
                reject(response);
            });
        });
    }
}
